import { useState, useMemo } from 'react';
import {
  useAdminApiProviders,
  useAdminNetworks,
  useCreateAdminNetwork,
  useUpdateAdminNetworkById,
  useDeleteAdminNetworkById
} from '../../../api/admin';
import { toast } from 'react-hot-toast';

// Backend response types
interface BackendNetwork {
  id: number;
  created_at: string;
  updated_at: string;
  name: string;
  plan_id: number;
  logo: string | null;
  logo_url: string;
  provider: number;
}

interface BackendProvider {
  id: number;
  name: string;
  base_url: string;
  auth_type: string;
  is_active: boolean;
  rate_limit_per_minute: number;
  last_health_check: string | null;
  priority: number;
  services_count: number;
}

interface Network {
  id: number;
  name: string;
  provider: number;
  providerName: string;
  logo: string;
  plan_id: number;
  createdAt: string;
  updatedAt: string;
}

export default function NetworkManagement() {
  // API Hooks
  const { data: providersResponse, isLoading: providersLoading } = useAdminApiProviders();
  const { data: networksData, isLoading: networksLoading, refetch: refetchNetworks } = useAdminNetworks();
  const createNetwork = useCreateAdminNetwork();
  const updateNetwork = useUpdateAdminNetworkById();
  const deleteNetwork = useDeleteAdminNetworkById();

  // Local State
  const [showModal, setShowModal] = useState(false);
  const [editingNetwork, setEditingNetwork] = useState<Network | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    provider: '',
    logo_url: '',
    plan_id: ''
  });

  // Extract providers from the nested response structure
  const providers = useMemo(() => {
    if (!providersResponse?.results?.data) return [];
    return providersResponse.results.data as BackendProvider[];
  }, [providersResponse]);

  // Map backend networks to frontend format
  const networks = useMemo(() => {
    if (!networksData || !Array.isArray(networksData)) return [];

    return networksData.map((network: BackendNetwork) => {
      const provider = providers.find(p => p.id === network.provider);
      return {
        id: network.id,
        name: network.name,
        provider: network.provider,
        providerName: provider?.name || `Provider ${network.provider}`,
        logo: network.logo_url || network.logo || '',
        plan_id: network.plan_id,
        createdAt: new Date(network.created_at).toLocaleDateString(),
        updatedAt: new Date(network.updated_at).toLocaleDateString()
      };
    });
  }, [networksData, providers]);

  // Filter networks
  const filteredNetworks = useMemo(() => {
    return networks.filter(network =>
      network.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      network.providerName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [networks, searchTerm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const payload = {
        name: formData.name,
        provider: parseInt(formData.provider),
        logo_url: formData.logo_url,
        plan_id: parseInt(formData.plan_id)
      };

      if (editingNetwork) {
        await updateNetwork.mutateAsync({
          id: editingNetwork.id,
          data: payload
        });
        toast.success('Network updated successfully!');
      } else {
        await createNetwork.mutateAsync(payload);
        toast.success('Network created successfully!');
      }

      refetchNetworks();
      resetForm();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message ||
        error?.message ||
        'Failed to save network';
      toast.error(errorMessage);
      console.error('Error saving network:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      provider: '',
      logo_url: '',
      plan_id: ''
    });
    setEditingNetwork(null);
    setShowModal(false);
  };

  const handleEdit = (network: Network) => {
    setEditingNetwork(network);
    setFormData({
      name: network.name,
      provider: network.provider.toString(),
      logo_url: network.logo,
      plan_id: network.plan_id.toString()
    });
    setShowModal(true);
  };

  const handleDelete = async (networkId: number) => {
    if (!confirm('Are you sure you want to delete this network?')) return;

    try {
      await deleteNetwork.mutateAsync(networkId);
      toast.success('Network deleted successfully!');
      refetchNetworks();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message ||
        error?.message ||
        'Failed to delete network';
      toast.error(errorMessage);
      console.error('Error deleting network:', error);
    }
  };

  // Loading state
  if (networksLoading || providersLoading) {
    return (
      <div className="p-4 md:p-6 space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-red-600 animate-spin"></i>
            <p className="mt-2 text-gray-600">Loading networks...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Network Management</h1>
          <p className="text-gray-600 mt-1">Manage telecommunications network providers</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="mt-4 md:mt-0 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-fill mr-2"></i>
          Add Network
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-100">
        <div className="max-w-md">
          <label className="block text-sm font-medium text-gray-700 mb-2">Search Networks</label>
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or provider"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Empty State */}
      {filteredNetworks.length === 0 && (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-100 text-center">
          <i className="ri-wifi-off-line text-5xl text-gray-300 mb-4"></i>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Networks Found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm ? 'Try adjusting your search terms' : 'Get started by adding your first network'}
          </p>
          {!searchTerm && (
            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <i className="ri-add-fill mr-2"></i>
              Add Network
            </button>
          )}
        </div>
      )}

      {/* Networks Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredNetworks.map((network) => (
          <div key={network.id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center space-x-3 mb-4">
              <img
                src={network.logo}
                alt={network.name}
                className="w-12 h-12 rounded-lg object-cover"
                onError={(e) => {
                  e.currentTarget.src = 'https://via.placeholder.com/48?text=N';
                }}
              />
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">{network.name}</h3>
                <p className="text-sm text-gray-500">{network.providerName}</p>
              </div>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Network ID:</span>
                <span className="font-medium">#{network.id}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Plan ID:</span>
                <span className="font-medium">#{network.plan_id}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Created:</span>
                <span className="font-medium">{network.createdAt}</span>
              </div>
            </div>

            <div className="flex space-x-2">
              <button
                onClick={() => handleEdit(network)}
                className="flex-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm whitespace-nowrap"
              >
                <i className="ri-edit-fill mr-1"></i>
                Edit
              </button>
              <button
                onClick={() => handleDelete(network.id)}
                className="flex-1 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm whitespace-nowrap"
              >
                <i className="ri-delete-bin-fill mr-1"></i>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingNetwork ? 'Edit Network' : 'Add New Network'}
                </h3>
                <button
                  onClick={resetForm}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <i className="ri-close-fill text-xl"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Network Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    placeholder="e.g., MTN Nigeria"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">API Provider</label>
                  <select
                    value={formData.provider}
                    onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    required
                  >
                    <option value="">Select a provider</option>
                    {providers.map((provider) => (
                      <option key={provider.id} value={provider.id}>
                        {provider.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Plan ID</label>
                  <input
                    type="number"
                    value={formData.plan_id}
                    onChange={(e) => setFormData({ ...formData, plan_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    placeholder="e.g., 1"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Logo URL</label>
                  <input
                    type="url"
                    value={formData.logo_url}
                    onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    placeholder="https://example.com/logo.png"
                    required
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    disabled={createNetwork.isPending || updateNetwork.isPending}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createNetwork.isPending || updateNetwork.isPending ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        {editingNetwork ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      <>{editingNetwork ? 'Update Network' : 'Create Network'}</>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
